import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Chat from '../Chat/Chat'
import Header from 'components/containers/Header'
import { styleByScreenSize } from './config'
import CloseWidgetModal from '../../closeWidgetModal/CloseWidgetModal'
import { WebSocketsSharedConsumer } from 'integrations/WebSockets'
import classes from './styles.module.scss'
import 'app/App.css'
import { SettingsType } from 'models/SettingsType'
import { MessageType } from 'models/MessageType'
import { PersistentMenuType } from 'models/PersistentMenuType'
import { IframeSettingsType } from 'models/IframeSettingsType'
import { DatePickerSettingsType } from '../../../models/DatePickerSettingsType'
import ChangeLanguageModal from '../../changeLanguageModal/ChangeLanguageModal'

interface Props {
  isMobile: boolean
  settings: SettingsType
  parentURL: string
  messages: MessageType[]
  setIframe: (p: IframeSettingsType) => void
  isUserIdle: boolean
  isEmpowerPre: boolean
  isEmpowerPost: boolean
  isPanoramaPost: boolean
  isTextDisabled: boolean
  toggleWidget: () => void
  resizeWidget: (props: boolean) => void
  iframeSettings: IframeSettingsType
  updateMessages: () => void
  setClickableChat: Function
  setIsNeedCloseWidget: (value: boolean) => void
  isNeedCloseWidget: boolean
  hasPersistentMenu: boolean
  persistentMenu: PersistentMenuType
  isMaximized: boolean
  autofilledText: boolean
  showLoader: boolean
  isCloseWidgetModalOpened: boolean
  setIsCloseWidgetModalOpened: (p: boolean) => void
  handleHiddenWidget?: () => void
  firstUnreadMessageId: string
  setFirstUnreadMessageId: (a: boolean | null) => void
  handleSoundNotification: () => void
  isSoundNotification?: boolean
  avatarLogo: string
  closePopupText: string
  closePopupScheduleGCText: string
  shouldShowClosePopupScheduleGCText: boolean
  keepChattingButtonText: string
  closeChatButtonText: string
  setIsImageZoomed: (props: boolean) => void
  imageModalSettings: any
  setImageModalSettings: (s: any) => void
  datePickerSettings: DatePickerSettingsType
  setDatePickerSettings: (s: DatePickerSettingsType) => void
  updateMessagesState: (id: string, value: string | number) => void
  passedOrSkippedFlowComponentsId: string[]
  setPassedOrSkippedFlowComponentsId: (id: string) => void
  updateDisablingButtonsByPersistentMenuUse: () => void
  startTiming: number
  widgetLoadedStartTiming: number
  isDDRumTimingSent: boolean
  setIsDDRumTimingSent: (p: boolean) => void
  isOpenSwitchLanguageModal: boolean
  setIsOpenSwitchLanguageModal: (p: boolean) => void
  switchLanguagePopupText: string
  continueSwitchingLanguageButtonText: string
  cancelSwitchingLanguageButtonText: string
  widgetIframeSource: any
  setWidgetIframeSource: (p: any) => void
  language: string
}

const Container: React.FC<Props> = props => {
  const [anchorMenu, setAnchorMenu] = useState(null)
  const [device, setDevice] = useState({})

  const {
    isMobile,
    settings,
    parentURL,
    messages,
    setIframe,
    isUserIdle,
    isEmpowerPre,
    isEmpowerPost,
    isPanoramaPost,
    isTextDisabled,
    toggleWidget,
    iframeSettings,
    updateMessages,
    setClickableChat,
    setIsNeedCloseWidget,
    isNeedCloseWidget,
    persistentMenu,
    isMaximized,
    autofilledText,
    showLoader,
    isCloseWidgetModalOpened,
    setIsCloseWidgetModalOpened,
    firstUnreadMessageId,
    setFirstUnreadMessageId,
    avatarLogo,
    closePopupText,
    closePopupScheduleGCText,
    shouldShowClosePopupScheduleGCText,
    keepChattingButtonText,
    closeChatButtonText,
    setIsImageZoomed,
    imageModalSettings,
    setImageModalSettings,
    datePickerSettings,
    setDatePickerSettings,
    updateMessagesState,
    passedOrSkippedFlowComponentsId,
    setPassedOrSkippedFlowComponentsId,
    updateDisablingButtonsByPersistentMenuUse,
    isOpenSwitchLanguageModal,
    setIsOpenSwitchLanguageModal,
    switchLanguagePopupText,
    continueSwitchingLanguageButtonText,
    cancelSwitchingLanguageButtonText,
    widgetIframeSource,
    setWidgetIframeSource,
    language,
    startTiming,
    widgetLoadedStartTiming,
    isDDRumTimingSent,
    setIsDDRumTimingSent,
  } = props

  const [isIframeContentLoaded, setIsIframeContentLoaded] = useState<boolean>(false)
  const [isLoadingIframeData, setIsLoadingIframeData] = useState<boolean>(false)
  const persistentMenuButtonRef = useRef<HTMLButtonElement>(null)
  const textMessageRef = useRef(null)

  const chatContainer = useCallback(node => {
    if (!node) return;

    setTimeout(() => {
      setDevice({
        width: node.getBoundingClientRect().width,
        height: node.getBoundingClientRect().height
      })
    }, 250)
  }, [isMaximized])

  const containerStyle = useMemo(() => {
    return styleByScreenSize(isMobile)
  }, [])

  const [ isScrollChat, setIsScrollChat ] = useState(true)

  useEffect(() => {
    if ((iframeSettings.isIframeOpen && iframeSettings.iframeSrc) || isNeedCloseWidget || isOpenSwitchLanguageModal) {
      setIsScrollChat(false)
      return
    }

    setIsScrollChat(true)
  }, [iframeSettings.isIframeOpen, isNeedCloseWidget, isOpenSwitchLanguageModal])

  const closePersistentMenu = () => {
    if (anchorMenu) {
      return setAnchorMenu(null)
    }
  }

  useEffect(() => {
    return () => {
      setFirstUnreadMessageId(null)
    }
  }, [])

  return (
    <WebSocketsSharedConsumer>
      {value => (
        <div
          ref={chatContainer}
          className={isPanoramaPost ? classes.panoramaContainer : classes.container}
          style={containerStyle}
          onClick={closePersistentMenu}
        >
          {settings && (
            <Header
              setIframe={setIframe}
              isUserIdle={isUserIdle}
              isPanoramaPost={isPanoramaPost}
              isEmpowerPost={isEmpowerPost}
              iframeSettings={iframeSettings}
              settings={settings}
              postMessage={value.postMessage}
              isMaximized={isMaximized}
              device={device}
              persistentMenu={persistentMenu}
              setIsScrollChat={setIsScrollChat}
              setClickableChat={setClickableChat}
              isCloseWidgetModalOpened={isCloseWidgetModalOpened}
              anchorMenu={anchorMenu}
              setAnchorMenu={setAnchorMenu}
              isIframeContentLoaded={isIframeContentLoaded}
              setIsIframeContentLoaded={setIsIframeContentLoaded}
              setIsLoadingIframeData={setIsLoadingIframeData}
              isLoadingIframeData={isLoadingIframeData}
              persistentMenuButtonRef={persistentMenuButtonRef}
              updateDisablingButtonsByPersistentMenuUse={updateDisablingButtonsByPersistentMenuUse}
              textMessageRef={textMessageRef}
            />
          )}
          {value && (
            <Chat
              device={device}
              isMobile={isMobile}
              isUserIdle={isUserIdle}
              messages={messages}
              settings={settings}
              parentURL={parentURL}
              setIframe={setIframe}
              isEmpowerPre={isEmpowerPre}
              isEmpowerPost={isEmpowerPost}
              isPanoramaPost={isPanoramaPost}
              isScrollChat={isScrollChat}
              imageModalSettings={imageModalSettings}
              setImageModalSettings={setImageModalSettings}
              datePickerSettings={datePickerSettings}
              setDatePickerSettings={setDatePickerSettings}
              iframeSettings={iframeSettings}
              startData={value.startData}
              connected={value.connected}
              postMessage={value.postMessage}
              isTextDisabled={isTextDisabled}
              autofilledText={autofilledText}
              updateMessages={updateMessages}
              chatId={value.chatId}
              botId={value.botId}
              showTyping={value.showTyping}
              updateTyping={value.updateTyping}
              isMaximized={isMaximized}
              initializeConversation={value.initializeConversation}
              getMoreChatHistory={value.getMoreChatHistory}
              showLoader={showLoader}
              anchorMenu={anchorMenu}
              setAnchorMenu={setAnchorMenu}
              firstUnreadMessageId={firstUnreadMessageId}
              avatarLogo={avatarLogo}
              setIsIframeContentLoaded={setIsIframeContentLoaded}
              setIsImageZoomed={setIsImageZoomed}
              setIsLoadingIframeData={setIsLoadingIframeData}
              isLoadingIframeData={isLoadingIframeData}
              updateMessagesState={updateMessagesState}
              passedOrSkippedFlowComponentsId={passedOrSkippedFlowComponentsId}
              setPassedOrSkippedFlowComponentsId={setPassedOrSkippedFlowComponentsId}
              textMessageRef={textMessageRef}
              startTiming={startTiming}
              widgetLoadedStartTiming={widgetLoadedStartTiming}
              isDDRumTimingSent={isDDRumTimingSent}
              setIsDDRumTimingSent={setIsDDRumTimingSent}
              persistentMenuButtonRef={persistentMenuButtonRef}
              widgetIframeSource={widgetIframeSource}
              setWidgetIframeSource={setWidgetIframeSource}
              language={language}
            />
          )}
          {isNeedCloseWidget && (
            <CloseWidgetModal
              toggleWidget={toggleWidget}
              setIsNeedCloseWidget={setIsNeedCloseWidget}
              setIsCloseWidgetModalOpened={setIsCloseWidgetModalOpened}
              setIframe={setIframe}
              chatId={value.chatId}
              botId={value.botId}
              closePopupText={closePopupText}
              closePopupScheduleGCText={closePopupScheduleGCText}
              shouldShowClosePopupScheduleGCText={shouldShowClosePopupScheduleGCText}
              keepChattingButtonText={keepChattingButtonText}
              closeChatButtonText={closeChatButtonText}
              iframeSettings={iframeSettings}
              isEmpowerPre={isEmpowerPre}
              isEmpowerPost={isEmpowerPost}
              persistentMenuButtonRef={persistentMenuButtonRef}
            />
          )}
          {isOpenSwitchLanguageModal && (
            <ChangeLanguageModal
              chatId={value.chatId}
              botId={value.botId}
              setIsOpenSwitchLanguageModal={setIsOpenSwitchLanguageModal}
              switchLanguagePopupText={switchLanguagePopupText}
              continueSwitchingLanguageButtonText={continueSwitchingLanguageButtonText}
              cancelSwitchingLanguageButtonText={cancelSwitchingLanguageButtonText}
            />
          )}
        </div>
      )}
    </WebSocketsSharedConsumer>
  )
}

export default Container
